import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button, Input, Select, Textarea } from '@components/ui';
import type { Product } from '../types/product.types';
import { SEGMENTS, getFamiliesBySegment } from '../constants/taxCategories';

const productSchema = z.object({
  code: z.string().min(1, 'El código es requerido').max(20, 'Máximo 20 caracteres'),
  name: z.string().min(2, 'Mínimo 2 caracteres').max(500, 'Máximo 500 caracteres'),
  description: z.string().min(2, 'Mínimo 2 caracteres').max(500, 'Máximo 500 caracteres'),
  unitPrice: z.number().min(0, 'El precio debe ser mayor o igual a 0'),
  taxRate: z.number().min(0, 'La tasa de impuesto debe ser mayor o igual a 0').max(100, 'La tasa de impuesto debe estar entre 0 y 100'),
  category: z.string().min(1, 'La categoría es requerida'),
  unit: z.string().min(1, 'La unidad de medida es requerida'),
  segment: z.string().optional(),
  family: z.string().optional(),
  stock: z.number().min(0, 'El stock debe ser mayor o igual a 0').optional(),
  isActive: z.boolean(),
});

type ProductFormData = z.infer<typeof productSchema>;

interface ProductFormProps {
  product?: Product;
  onSubmit: (data: Partial<Product>) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

export function ProductForm({ product, onSubmit, onCancel, isLoading }: ProductFormProps) {
  const [selectedSegment, setSelectedSegment] = useState<string>('');
  const [availableFamilies, setAvailableFamilies] = useState<any[]>([]);

  // Map Product type to ProductFormData
  const getDefaultValues = (): Partial<ProductFormData> => {
    if (!product) {
      return {
        unitPrice: 0,
        taxRate: 7,
        isActive: true,
        stock: 0,
        segment: '',
        family: '',
        unit: 'und',
      };
    }

    // Extract numeric tax rate from taxCode (e.g., "ITBMS7" -> 7, "IGV18" -> 18, "NOTAX" -> 0)
    const extractTaxRate = (taxCode?: string): number => {
      if (!taxCode) return 7;
      if (taxCode.toUpperCase() === 'NOTAX') return 0;
      const match = taxCode.match(/\d+/);
      return match ? parseInt(match[0], 10) : 7;
    };

    return {
      code: product.sku,
      name: product.name,
      description: product.description || '',
      unitPrice: product.price,
      taxRate: extractTaxRate(product.taxCode),
      category: product.category || '',
      unit: product.unitCode || 'und',
      segment: product.segment || '',
      family: product.family || '',
      stock: 0, // Stock is not in Product type from backend
      isActive: !product.isDeleted,
    };
  };

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm<ProductFormData>({
    resolver: zodResolver(productSchema),
    mode: 'onChange',
    defaultValues: getDefaultValues(),
  });

  // Watch segment changes
  const segmentValue = watch('segment');

  // Reset form values when product changes (for edit mode)
  useEffect(() => {
    const defaultValues = getDefaultValues();
    reset(defaultValues);

    // Initialize segment and families for editing
    if (product && product.segment) {
      setSelectedSegment(product.segment);
      const families = getFamiliesBySegment(product.segment);
      setAvailableFamilies(families);
    } else {
      setSelectedSegment('');
      setAvailableFamilies([]);
    }
  }, [product, reset]);

  // Update families when segment changes
  useEffect(() => {
    if (segmentValue) {
      const families = getFamiliesBySegment(segmentValue);
      setAvailableFamilies(families);
      setSelectedSegment(segmentValue);
    } else {
      setAvailableFamilies([]);
      setValue('family', '');
    }
  }, [segmentValue, setValue]);

  // Transform ProductFormData to Product format
  const handleFormSubmit = (data: ProductFormData) => {
    const productData: Partial<Product> = {
      sku: data.code,
      name: data.name,
      description: data.description,
      price: data.unitPrice,
      taxCode: data.taxRate === 0 ? 'NOTAX' : `ITBMS${data.taxRate}`,
      category: data.category,
      unitCode: data.unit,
      segment: data.segment || undefined,
      family: data.family || undefined,
      isDeleted: !data.isActive,
      isService: data.category === 'Servicios',
      priceIncludesTax: false,
      currency: 'USD',
    };

    console.log('📤 Product Form Submit Data:', {
      ...productData,
      hasSegment: !!data.segment,
      hasFamily: !!data.family,
      segmentValue: data.segment,
      familyValue: data.family,
    });

    onSubmit(productData);
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-5">
      {/* Fila 1: Código, Categoría, Nombre */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Input
          label="Código"
          placeholder="PROD-001"
          error={errors.code?.message}
          maxLength={20}
          {...register('code')}
        />
        <Select
          label="Categoría"
          error={errors.category?.message}
          options={[
            { value: 'Servicios', label: 'Servicios' },
            { value: 'Productos', label: 'Productos' },
            { value: 'Software', label: 'Software' },
            { value: 'Consultoría', label: 'Consultoría' },
            { value: 'Hardware', label: 'Hardware' },
            { value: 'Otro', label: 'Otro' },
          ]}
          {...register('category')}
        />
        <Input
          label="Nombre del Producto"
          placeholder="Nombre del producto"
          error={errors.name?.message}
          maxLength={500}
          {...register('name')}
        />
      </div>

      {/* Fila 2: Descripción */}
      <Textarea
        label="Descripción"
        placeholder="Descripción del producto o servicio"
        error={errors.description?.message}
        maxLength={500}
        rows={2}
        {...register('description')}
      />

      {/* Fila 3: Precio, Impuesto, Unidad, Stock */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Input
          label="Precio"
          type="number"
          step="0.000001"
          placeholder="0.00"
          min="0"
          inputMode="decimal"
          error={errors.unitPrice?.message}
          {...register('unitPrice', { valueAsNumber: true })}
        />
        <Select
          label="Impuesto"
          error={errors.taxRate?.message}
          options={[
            { value: '0', label: '0%' },
            { value: '7', label: '7%' },
            { value: '10', label: '10%' },
            { value: '15', label: '15%' },
          ]}
          {...register('taxRate', { valueAsNumber: true })}
        />
        <Select
          label="Unidad"
          error={errors.unit?.message}
          options={[
            { value: 'und', label: 'Unidad' },
            { value: 'Persona', label: 'Persona' },
            { value: 'Grupo', label: 'Grupo' },
            { value: 'Paquete', label: 'Paquete' },
            { value: 'Kit', label: 'Kit' },
            { value: 'Caja', label: 'Caja' },
            { value: 'Docena', label: 'Docena' },
            { value: 'Viaje', label: 'Viaje' },
            { value: 'Visita', label: 'Visita' },
            { value: 'Hora', label: 'Hora' },
            { value: 'Dia', label: 'Día' },
            { value: 'Semana', label: 'Semana' },
            { value: 'Mes', label: 'Mes' },
            { value: 'Ano', label: 'Año' },
            { value: 'Actividad', label: 'Actividad' },
            { value: 'Hora trabajo', label: 'Hora de trabajo' },
            { value: 'g', label: 'Gramos (g)' },
            { value: 'kg', label: 'Kilogramos (kg)' },
            { value: 'lb', label: 'Libras (lb)' },
            { value: 'oz', label: 'Onzas (oz)' },
            { value: 't', label: 'Tonelada (t)' },
            { value: 'cm', label: 'Centímetros (cm)' },
            { value: 'm', label: 'Metros (m)' },
            { value: 'km', label: 'Kilómetros (km)' },
            { value: 'm2', label: 'M² (m2)' },
            { value: 'ha', label: 'Hectárea (ha)' },
            { value: 'ml', label: 'Mililitros (ml)' },
            { value: 'l', label: 'Litros (l)' },
            { value: 'm3', label: 'M³ (m3)' },
            { value: 'gal', label: 'Galón (gal)' },
            { value: 'kWh', label: 'Kilowatt hora (kWh)' },
          ]}
          {...register('unit')}
        />
        <Input
          label="Stock"
          type="number"
          placeholder="0"
          error={errors.stock?.message}
          {...register('stock', { valueAsNumber: true })}
        />
      </div>

      {/* Fila 4: Segmento, Familia, Estado */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Select
          label="Segmento (Opcional)"
          error={errors.segment?.message}
          {...register('segment')}
        >
          <option value="">Seleccione un segmento</option>
          {SEGMENTS.map((segment) => (
            <option key={segment.Code} value={segment.Code.toString()}>
              {segment.Code} - {segment.Description}
            </option>
          ))}
        </Select>

        <Select
          label="Familia (Opcional)"
          error={errors.family?.message}
          disabled={!selectedSegment}
          {...register('family')}
        >
          <option value="">
            {selectedSegment ? 'Seleccione una familia' : 'Primero seleccione segmento'}
          </option>
          {availableFamilies.map((family) => (
            <option key={family.Code} value={family.Code}>
              {family.Code} - {family.Description}
            </option>
          ))}
        </Select>

        <div className="flex items-end pb-2">
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              id="isActive"
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-secondary-300 rounded"
              {...register('isActive')}
            />
            <span className="text-sm font-medium text-secondary-700 dark:text-secondary-300">
              Producto Activo
            </span>
          </label>
        </div>
      </div>

      {/* Botones */}
      <div className="flex flex-col gap-2 sm:flex-row sm:justify-end sm:gap-3 pt-3 border-t border-secondary-200 dark:border-secondary-700 [&>*]:w-full sm:[&>*]:w-auto">
        <Button type="button" variant="ghost" onClick={onCancel} disabled={isLoading}>
          Cancelar
        </Button>
        <Button type="submit" variant="primary" isLoading={isLoading}>
          {product ? 'Actualizar' : 'Crear'} Producto
        </Button>
      </div>
    </form>
  );
}
